import { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query, setDoc } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { ArrowDown, ArrowUp, ImageOff, Plus, Save, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { db, storage } from '../../firebase/config';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import { normalizeCotwVisibility } from '../../lib/cotwVisibility';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { NativeSelect } from '../ui/select-native';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import PageHeader from './shell/PageHeader';

const Field = ({ label, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const Section = ({ title, description, children }) => (
  <Card className="border-border/60 bg-background/40">
    <CardHeader>
      <CardTitle className="font-display text-lg">{title}</CardTitle>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </CardHeader>
    <CardContent>
      <div className="grid gap-5">{children}</div>
    </CardContent>
  </Card>
);

const ContentManagement = () => {
  const { config } = useSiteConfig();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroFile, setHeroFile] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const [tournaments, setTournaments] = useState([]);

  useEffect(() => {
    const loadTournaments = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'tournaments'), orderBy('startDate', 'desc'))
        );
        setTournaments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch {
        // silent — dropdown just stays empty
      }
    };
    loadTournaments();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'siteConfig', 'main'));
        const remote = snap.exists() ? snap.data() : {};
        const merged = {
          compOfTheWeek: {
            published: normalizeCotwVisibility(remote),
          },
          hero: { ...config.hero, ...(remote.hero || {}) },
          landing: {
            hero: { ...config.landing.hero, ...(remote.landing?.hero || {}) },
            ledger: { ...config.landing.ledger, ...(remote.landing?.ledger || {}) },
            mapOfDay: { ...config.landing.mapOfDay, ...(remote.landing?.mapOfDay || {}) },
            youtube: { ...config.landing.youtube, ...(remote.landing?.youtube || {}) },
            community: { ...config.landing.community, ...(remote.landing?.community || {}) },
          },
          staff: {
            description: remote.staff?.description ?? config.staff?.description ?? '',
          },
          footer: {
            tagline: remote.footer?.tagline ?? config.footer.tagline ?? '',
            copyright: remote.footer?.copyright ?? config.footer.copyright ?? '',
            columns: remote.footer?.columns || config.footer.columns || [],
            socials: remote.footer?.socials || config.footer.socials,
          },
        };
        setForm(merged);
      } catch (err) {
        toast.error('Could not load site content.', { description: err.message });
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAt = (path, value) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      const keys = path.split('.');
      let node = next;
      for (let i = 0; i < keys.length - 1; i++) {
        node[keys[i]] = node[keys[i]] ?? {};
        node = node[keys[i]];
      }
      node[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setSocial = (platform, url) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      const list = Array.isArray(next.footer?.socials) ? [...next.footer.socials] : [];
      const idx = list.findIndex((s) => s.platform === platform);
      if (idx >= 0) list[idx] = { ...list[idx], url };
      else list.push({ platform, url });
      next.footer = { ...(next.footer || {}), socials: list };
      return next;
    });
  };

  const socialUrl = (platform) =>
    (form?.footer?.socials || []).find((s) => s.platform === platform)?.url || '';

  const updateColumns = (fn) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.footer = next.footer || {};
      const cols = Array.isArray(next.footer.columns) ? [...next.footer.columns] : [];
      next.footer.columns = fn(cols) ?? cols;
      return next;
    });
  };

  const moveItem = (arr, idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return arr;
    const copy = [...arr];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    return copy;
  };

  const addColumn = () => updateColumns((cols) => [...cols, { heading: 'New column', links: [] }]);
  const removeColumn = (ci) => updateColumns((cols) => cols.filter((_, i) => i !== ci));
  const moveColumn = (ci, dir) => updateColumns((cols) => moveItem(cols, ci, dir));
  const setColumnHeading = (ci, heading) =>
    updateColumns((cols) => cols.map((c, i) => (i === ci ? { ...c, heading } : c)));

  const addLink = (ci) =>
    updateColumns((cols) =>
      cols.map((c, i) =>
        i === ci ? { ...c, links: [...(c.links || []), { label: '', path: '' }] } : c
      )
    );
  const removeLink = (ci, li) =>
    updateColumns((cols) =>
      cols.map((c, i) =>
        i === ci ? { ...c, links: (c.links || []).filter((_, k) => k !== li) } : c
      )
    );
  const moveLink = (ci, li, dir) =>
    updateColumns((cols) =>
      cols.map((c, i) => (i === ci ? { ...c, links: moveItem(c.links || [], li, dir) } : c))
    );
  const setLinkField = (ci, li, field, value) =>
    updateColumns((cols) =>
      cols.map((c, i) =>
        i === ci
          ? {
              ...c,
              links: (c.links || []).map((l, k) => (k === li ? { ...l, [field]: value } : l)),
            }
          : c
      )
    );

  const pickHero = (f) => {
    if (!f) return;
    setHeroFile(f);
    setHeroPreview(URL.createObjectURL(f));
  };

  const clearHeroImage = () => {
    setHeroFile(null);
    setHeroPreview(null);
    setAt('landing.hero.backgroundImageUrl', '');
    setAt('landing.hero.backgroundStoragePath', '');
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = structuredClone(form);

      if (heroFile) {
        const path = `site-content/hero_${Date.now()}_${heroFile.name}`;
        const sref = ref(storage, path);
        await uploadBytes(sref, heroFile);
        const url = await getDownloadURL(sref);
        const oldPath = form.landing?.hero?.backgroundStoragePath;
        payload.landing.hero.backgroundImageUrl = url;
        payload.landing.hero.backgroundStoragePath = path;
        if (oldPath && oldPath !== path) {
          try {
            await deleteObject(ref(storage, oldPath));
          } catch {
            // non-fatal
          }
        }
      }

      await setDoc(doc(db, 'siteConfig', 'main'), payload, { merge: true });
      setHeroFile(null);
      setHeroPreview(null);
      toast.success('Site content saved.');
    } catch (err) {
      toast.error('Save failed.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="pb-16">
        <PageHeader eyebrow="Site content" title="Content management" description="Loading…" />
      </div>
    );
  }

  const heroImgSrc = heroPreview || form.landing.hero.backgroundImageUrl;

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow="Site content"
        title="Content management"
        description="Edit labels, links, and imagery shown on the public landing page."
      />

      <div className="grid gap-6">
        <Section
          title="Hero"
          description="Top of the landing page — dossier line, headline, tagline, buttons, background photo."
        >
          <Field label="Background image" hint="Used as the faded hero backdrop.">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => pickHero(e.target.files?.[0])}
              className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-xs file:font-medium file:uppercase file:tracking-[0.15em] file:text-secondary-foreground hover:file:bg-secondary/80"
            />
            {heroImgSrc ? (
              <div className="mt-3 space-y-2">
                <img
                  src={heroImgSrc}
                  alt=""
                  className="h-48 w-full rounded-md border border-border/60 object-cover"
                />
                <Button type="button" variant="ghost" size="sm" onClick={clearHeroImage}>
                  <ImageOff className="h-3.5 w-3.5" />
                  Remove (revert to default)
                </Button>
              </div>
            ) : (
              <div className="mt-3 flex h-48 items-center justify-center rounded-md border border-dashed border-border/60 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Default image
              </div>
            )}
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Dossier prefix">
              <Input
                value={form.landing.hero.dossierPrefix || ''}
                onChange={(e) => setAt('landing.hero.dossierPrefix', e.target.value)}
              />
            </Field>
            <Field label="Coordinates">
              <Input
                value={form.landing.hero.coordinates || ''}
                onChange={(e) => setAt('landing.hero.coordinates', e.target.value)}
              />
            </Field>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Headline line 1">
              <Input
                value={form.landing.hero.headlineLine1 || ''}
                onChange={(e) => setAt('landing.hero.headlineLine1', e.target.value)}
              />
            </Field>
            <Field label="Headline line 2" hint="Rendered in dim accent style.">
              <Input
                value={form.landing.hero.headlineLine2 || ''}
                onChange={(e) => setAt('landing.hero.headlineLine2', e.target.value)}
              />
            </Field>
          </div>

          <Field label="Tagline">
            <Textarea
              rows={2}
              value={form.hero.tagline || ''}
              onChange={(e) => setAt('hero.tagline', e.target.value)}
            />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Primary button label">
              <Input
                value={form.hero.primaryCta?.label || ''}
                onChange={(e) => setAt('hero.primaryCta.label', e.target.value)}
              />
            </Field>
            <Field label="Primary button link">
              <Input
                value={form.hero.primaryCta?.path || ''}
                onChange={(e) => setAt('hero.primaryCta.path', e.target.value)}
                placeholder="/tournaments"
              />
            </Field>
            <Field label="Secondary button label">
              <Input
                value={form.hero.secondaryCta?.label || ''}
                onChange={(e) => setAt('hero.secondaryCta.label', e.target.value)}
              />
            </Field>
            <Field label="Secondary button link">
              <Input
                value={form.hero.secondaryCta?.path || ''}
                onChange={(e) => setAt('hero.secondaryCta.path', e.target.value)}
                placeholder="/stream"
              />
            </Field>
          </div>
        </Section>

        <Section
          title="Tournaments ledger"
          description="Active & upcoming tournaments list. Hides automatically when there are no active or upcoming tournaments."
        >
          <Field
            label="Show section"
            hint="Turn off to hide the tournaments list entirely, even when tournaments exist."
          >
            <div className="flex h-10 items-center gap-3">
              <Switch
                checked={form.landing.ledger.enabled !== false}
                onCheckedChange={(v) => setAt('landing.ledger.enabled', v)}
              />
              <span className="text-sm text-muted-foreground">
                {form.landing.ledger.enabled !== false ? 'Visible' : 'Hidden'}
              </span>
            </div>
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Eyebrow">
              <Input
                value={form.landing.ledger.eyebrow || ''}
                onChange={(e) => setAt('landing.ledger.eyebrow', e.target.value)}
              />
            </Field>
            <Field label="Title">
              <Input
                value={form.landing.ledger.title || ''}
                onChange={(e) => setAt('landing.ledger.title', e.target.value)}
              />
            </Field>
            <Field label="View-all link label">
              <Input
                value={form.landing.ledger.viewAllLabel || ''}
                onChange={(e) => setAt('landing.ledger.viewAllLabel', e.target.value)}
              />
            </Field>
            <Field label="View-all link path">
              <Input
                value={form.landing.ledger.viewAllPath || ''}
                onChange={(e) => setAt('landing.ledger.viewAllPath', e.target.value)}
                placeholder="/tournaments"
              />
            </Field>
            <Field
              label="Featured tournament"
              hint="Pinned to the top with a badge. Completed tournaments never show — pick an active or upcoming one."
            >
              <NativeSelect
                value={form.landing.ledger.featuredTournamentId || ''}
                onChange={(e) => setAt('landing.ledger.featuredTournamentId', e.target.value)}
              >
                <option value="">— None —</option>
                {tournaments.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name || 'Untitled'}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Featured badge label">
              <Input
                value={form.landing.ledger.featuredBadge || ''}
                onChange={(e) => setAt('landing.ledger.featuredBadge', e.target.value)}
                placeholder="Featured"
              />
            </Field>
          </div>
        </Section>

        <Section title="Map of the day" description="Daily minigame promo card.">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Eyebrow">
              <Input
                value={form.landing.mapOfDay.eyebrow || ''}
                onChange={(e) => setAt('landing.mapOfDay.eyebrow', e.target.value)}
              />
            </Field>
            <Field label="Title">
              <Input
                value={form.landing.mapOfDay.title || ''}
                onChange={(e) => setAt('landing.mapOfDay.title', e.target.value)}
              />
            </Field>
          </div>
          <Field label="Description">
            <Textarea
              rows={3}
              value={form.landing.mapOfDay.description || ''}
              onChange={(e) => setAt('landing.mapOfDay.description', e.target.value)}
            />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="CTA label">
              <Input
                value={form.landing.mapOfDay.ctaLabel || ''}
                onChange={(e) => setAt('landing.mapOfDay.ctaLabel', e.target.value)}
              />
            </Field>
            <Field label="CTA link">
              <Input
                value={form.landing.mapOfDay.ctaPath || ''}
                onChange={(e) => setAt('landing.mapOfDay.ctaPath', e.target.value)}
                placeholder="/map-guessing"
              />
            </Field>
          </div>
        </Section>

        <Section title="YouTube" description="Promo card linking to a YouTube channel or video.">
          <Field
            label="Show section"
            hint="Turn off to hide the YouTube card from the landing page."
          >
            <div className="flex h-10 items-center gap-3">
              <Switch
                checked={form.landing.youtube?.enabled === true}
                onCheckedChange={(v) => setAt('landing.youtube.enabled', v)}
              />
              <span className="text-sm text-muted-foreground">
                {form.landing.youtube?.enabled === true ? 'Visible' : 'Hidden'}
              </span>
            </div>
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Eyebrow">
              <Input
                value={form.landing.youtube?.eyebrow || ''}
                onChange={(e) => setAt('landing.youtube.eyebrow', e.target.value)}
              />
            </Field>
            <Field label="Title">
              <Input
                value={form.landing.youtube?.title || ''}
                onChange={(e) => setAt('landing.youtube.title', e.target.value)}
              />
            </Field>
          </div>
          <Field label="Description">
            <Textarea
              rows={3}
              value={form.landing.youtube?.description || ''}
              onChange={(e) => setAt('landing.youtube.description', e.target.value)}
            />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="CTA label">
              <Input
                value={form.landing.youtube?.ctaLabel || ''}
                onChange={(e) => setAt('landing.youtube.ctaLabel', e.target.value)}
              />
            </Field>
            <Field label="CTA link" hint="Use a full https:// URL to open YouTube in a new tab.">
              <Input
                value={form.landing.youtube?.ctaPath || ''}
                onChange={(e) => setAt('landing.youtube.ctaPath', e.target.value)}
                placeholder="https://youtube.com/@…"
              />
            </Field>
          </div>
        </Section>

        <Section
          title="Comp of the Week"
          description="Site publication controls only. Worker scheduling and event availability are managed separately."
        >
          <Field
            label="Publish page and navigation"
            hint="When unpublished, the public route redirects home and stale navigation links are removed."
          >
            <div className="flex h-10 items-center gap-3">
              <Switch
                checked={form.compOfTheWeek?.published === true}
                onCheckedChange={(published) => setAt('compOfTheWeek.published', published)}
              />
              <span className="text-sm text-muted-foreground">
                {form.compOfTheWeek?.published === true ? 'Published' : 'Unpublished'}
              </span>
            </div>
          </Field>
        </Section>

        <Section title="Community" description="Bottom section with Discord and players links.">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Eyebrow">
              <Input
                value={form.landing.community.eyebrow || ''}
                onChange={(e) => setAt('landing.community.eyebrow', e.target.value)}
              />
            </Field>
            <Field label="Headline line 1">
              <Input
                value={form.landing.community.headlineLine1 || ''}
                onChange={(e) => setAt('landing.community.headlineLine1', e.target.value)}
              />
            </Field>
            <Field label="Headline line 2" hint="Rendered in dim accent style.">
              <Input
                value={form.landing.community.headlineLine2 || ''}
                onChange={(e) => setAt('landing.community.headlineLine2', e.target.value)}
              />
            </Field>
          </div>
          <Field label="Description">
            <Textarea
              rows={3}
              value={form.landing.community.description || ''}
              onChange={(e) => setAt('landing.community.description', e.target.value)}
            />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Discord button label">
              <Input
                value={form.landing.community.discordLabel || ''}
                onChange={(e) => setAt('landing.community.discordLabel', e.target.value)}
              />
            </Field>
            <Field label="Discord URL" hint="Leave empty to hide the Discord button.">
              <Input
                value={socialUrl('discord')}
                onChange={(e) => setSocial('discord', e.target.value)}
                placeholder="https://discord.gg/…"
              />
            </Field>
            <Field
              label="YouTube URL"
              hint="Shown as a button in the footer. Leave empty to hide it."
            >
              <Input
                value={socialUrl('youtube')}
                onChange={(e) => setSocial('youtube', e.target.value)}
                placeholder="https://youtube.com/@…"
              />
            </Field>
            <Field label="Browse players label">
              <Input
                value={form.landing.community.browseLabel || ''}
                onChange={(e) => setAt('landing.community.browseLabel', e.target.value)}
              />
            </Field>
            <Field label="Browse players link">
              <Input
                value={form.landing.community.browsePath || ''}
                onChange={(e) => setAt('landing.community.browsePath', e.target.value)}
                placeholder="/users"
              />
            </Field>
          </div>
        </Section>

        <Section title="Staff page" description="Intro paragraph shown at the top of /staff.">
          <Field label="Description">
            <Textarea
              rows={3}
              value={form.staff?.description || ''}
              onChange={(e) => setAt('staff.description', e.target.value)}
            />
          </Field>
        </Section>

        <Section
          title="Footer"
          description="Tagline, copyright, and the link boxes shown in the site footer."
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Tagline">
              <Input
                value={form.footer?.tagline || ''}
                onChange={(e) => setAt('footer.tagline', e.target.value)}
              />
            </Field>
            <Field label="Copyright" hint="Rendered after the year. Example: KZcomp.com">
              <Input
                value={form.footer?.copyright || ''}
                onChange={(e) => setAt('footer.copyright', e.target.value)}
              />
            </Field>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
                Link boxes
              </span>
              <Button type="button" variant="outline" size="sm" onClick={addColumn}>
                <Plus className="h-3.5 w-3.5" />
                Add link box
              </Button>
            </div>

            {(form.footer?.columns || []).length === 0 && (
              <div className="rounded-md border border-dashed border-border/60 p-6 text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">
                No link boxes yet
              </div>
            )}

            {(form.footer?.columns || []).map((column, ci) => (
              <div
                key={ci}
                className="flex flex-col gap-4 rounded-md border border-border/60 bg-background/40 p-4"
              >
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Field label={`Box ${ci + 1} heading`}>
                      <Input
                        value={column.heading || ''}
                        onChange={(e) => setColumnHeading(ci, e.target.value)}
                        placeholder="e.g. Tournaments"
                      />
                    </Field>
                  </div>
                  <div className="flex items-center gap-1 pb-[2px]">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => moveColumn(ci, -1)}
                      disabled={ci === 0}
                      aria-label="Move up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => moveColumn(ci, 1)}
                      disabled={ci === (form.footer?.columns?.length || 0) - 1}
                      aria-label="Move down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeColumn(ci)}
                      aria-label="Remove link box"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  {(column.links || []).map((link, li) => (
                    <div
                      key={li}
                      className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-center"
                    >
                      <Input
                        value={link.label || ''}
                        onChange={(e) => setLinkField(ci, li, 'label', e.target.value)}
                        placeholder="Label"
                      />
                      <Input
                        value={link.path || ''}
                        onChange={(e) => setLinkField(ci, li, 'path', e.target.value)}
                        placeholder="/path or https://…"
                      />
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => moveLink(ci, li, -1)}
                          disabled={li === 0}
                          aria-label="Move link up"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => moveLink(ci, li, 1)}
                          disabled={li === (column.links?.length || 0) - 1}
                          aria-label="Move link down"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLink(ci, li)}
                          aria-label="Remove link"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="self-start"
                    onClick={() => addLink(ci)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add link
                  </Button>
                </div>
              </div>
            ))}

            <p className="text-xs text-muted-foreground">
              Empty link boxes and incomplete links (missing label or path) are hidden on the public
              site. Use absolute URLs (https://…) to open in a new tab.
            </p>
          </div>
        </Section>

        <div className="sticky bottom-4 z-10 flex justify-end">
          <Button onClick={handleSave} disabled={saving} size="lg">
            {saving ? <Upload className="h-4 w-4" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContentManagement;
